import { createFileRoute } from "@tanstack/react-router"
import MarketingLayout from "~/layouts/MarketingLayout"
import { ProductHero } from "~/components/Marketing/ProductHero"
import { ProductHowItWorks } from "~/components/Marketing/ProductHowItWorks"
import { ProblemSolution } from "~/components/Marketing/ProblemSolution"
import { ProductPricingTable } from "~/components/Pricing/ProductPricingTable"
import { ProductUseCases } from "~/components/Marketing/ProductUseCases"
import { FinalCTA } from "~/components/Marketing/FinalCTA"
import { BROLL_PRICING_CONFIG } from "~/config/pricing"

export const Route = createFileRoute("/b-roll")({
  component: BRollPage,
})

function BRollPage() {
  return (
    <MarketingLayout>
      <ProductHero
        title="Turn Product Photos Into 20 Dynamic Video Clips"
        subtitle="6 proven styles. Instant generation. Perfect for filling gaps in your ad creative."
        ctaText="Start Creating →"
        ctaHref="/auth/signin"
        secondaryText="Plans from $39/mo"
        socialProof={{
          quote: "Turned 3 product photos into 50 ad clips in one afternoon",
          author: "DTC Founder, Beauty Brand",
        }}
      />

      <ProductHowItWorks
        title="From Static Image to Video in 3 Steps"
        steps={[
          {
            number: 1,
            title: "Upload Product Images",
            bullets: ["1-3 product photos required", "Works with any image format", "White background recommended"],
          },
          {
            number: 2,
            title: "Choose Style Preset",
            bullets: [
              "Showcase (clean product display)",
              "Lifestyle (in-use scenarios)",
              "Problem/Solution (before/after)",
              "Unboxing (reveal animation)",
              "Comparison (vs competitor)",
              "Feature Highlight (zoom details)",
            ],
          },
          {
            number: 3,
            title: "Generate Batch",
            bullets: [
              "Creates 20 clips per batch (3-5 sec)",
              "AI animates images into motion",
              "Ready to drop into video editor",
            ],
          },
        ]}
      />

      <ProblemSolution
        title="Static Images Don't Convert Like Video"
        comparisons={[
          { problem: "Hiring videographer: $1000-3000/day", solution: "AI generation: $0.65 per clip" },
          { problem: "Scheduling product shoots", solution: "Generate from existing photos" },
          { problem: "Limited footage for editing", solution: "20 clips per batch, endless variations" },
          { problem: "Stale product shots in ads", solution: "New angles in minutes" },
        ]}
      />

      <div className="py-16 px-4 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">6 Styles for Every Ad Type</h2>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-6 bg-background rounded-lg border">
              <h3 className="text-xl font-semibold mb-2">1. Showcase</h3>
              <p className="text-muted-foreground mb-4">Clean rotation, product focus</p>
              <div className="aspect-video bg-muted/50 rounded flex items-center justify-center">
                <span className="text-muted-foreground">3x2 grid of example clips</span>
              </div>
            </div>

            <div className="p-6 bg-background rounded-lg border">
              <h3 className="text-xl font-semibold mb-2">2. Lifestyle</h3>
              <p className="text-muted-foreground mb-4">In-use scenarios, environment context</p>
              <div className="aspect-video bg-muted/50 rounded flex items-center justify-center">
                <span className="text-muted-foreground">3x2 grid of example clips</span>
              </div>
            </div>

            <div className="p-6 bg-background rounded-lg border">
              <h3 className="text-xl font-semibold mb-2">3. Problem/Solution</h3>
              <p className="text-muted-foreground mb-4">Before/after transformation</p>
              <div className="aspect-video bg-muted/50 rounded flex items-center justify-center">
                <span className="text-muted-foreground">3x2 grid of example clips</span>
              </div>
            </div>

            <div className="p-6 bg-background rounded-lg border">
              <h3 className="text-xl font-semibold mb-2">4. Unboxing</h3>
              <p className="text-muted-foreground mb-4">Reveal animation, excitement</p>
              <div className="aspect-video bg-muted/50 rounded flex items-center justify-center">
                <span className="text-muted-foreground">3x2 grid of example clips</span>
              </div>
            </div>

            <div className="p-6 bg-background rounded-lg border">
              <h3 className="text-xl font-semibold mb-2">5. Comparison</h3>
              <p className="text-muted-foreground mb-4">Side-by-side vs competitor</p>
              <div className="aspect-video bg-muted/50 rounded flex items-center justify-center">
                <span className="text-muted-foreground">3x2 grid of example clips</span>
              </div>
            </div>

            <div className="p-6 bg-background rounded-lg border">
              <h3 className="text-xl font-semibold mb-2">6. Feature Highlight</h3>
              <p className="text-muted-foreground mb-4">Zoom details, key features</p>
              <div className="aspect-video bg-muted/50 rounded flex items-center justify-center">
                <span className="text-muted-foreground">3x2 grid of example clips</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ProductPricingTable config={BROLL_PRICING_CONFIG} />

      <ProductUseCases
        title="Perfect for Filling Creative Gaps"
        useCases={[
          "Generate supporting footage for UGC/Hook videos",
          "Create product showcase reels for social",
          "Build video library from static catalog",
          "Test different visual styles across ads",
        ]}
      />

      <FinalCTA
        title="Turn Your Product Photos Into Motion"
        ctaText="Generate Your First Batch →"
        ctaHref="/auth/signin"
        supportingText="No shoots. No editing. Just upload and generate."
      />
    </MarketingLayout>
  )
}
